/**
 * AudienceImpactBadge Component
 * Displays audience impact value with colored icon indicator and tooltip.
 *
 * Levels: high (green), medium (orange), low (gray)
 *
 * Tooltip positioning (2026-05-15):
 *   • Uses `position: fixed` so it escapes the table's overflow clipping.
 *   • Prefers ABOVE the badge so the cursor never overlaps the panel.
 *   • Falls back to BELOW if there isn't enough room above.
 *   • Horizontally centred on the badge but CLAMPED inside the viewport
 *     so it can never get cut off at the left/right edge.
 *   • Measures the real rendered height after mount instead of assuming
 *     200 px so multi-line tooltips never overflow upward.
 */

import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const LEVEL_CONFIG = {
  high: {
    color: 'bg-green-500',
    textColor: 'text-green-400',
    tooltip: 'High Audience Impact\nSong reaches a large audience through influential stations and frequent plays.',
  },
  medium: {
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    tooltip: 'Moderate Audience Impact\nSong receives consistent exposure across monitored stations.',
  },
  low: {
    color: 'bg-gray-500',
    textColor: 'text-gray-400',
    tooltip: 'Low Audience Impact\nLimited radio exposure from station influence and play frequency.',
  },
};

const TOOLTIP_WIDTH = 260;
const CURSOR_OFFSET = 14;     // gap from badge so the cursor never covers the panel
const SAFE_MARGIN = 12;       // viewport edge safety

const AudienceImpactBadge = ({ value, level = 'low', compact = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0, ready: false });
  const badgeRef = useRef(null);
  const tooltipRef = useRef(null);
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.low;

  // Measure the real rendered tooltip height on every show so it auto-fits
  // multi-line content. Without this we assumed 200 px and longer tooltips
  // overflowed the top of the viewport (the user's bug).
  useLayoutEffect(() => {
    if (!showTooltip || !badgeRef.current || !tooltipRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight || 80;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    let y;
    if (spaceAbove >= tooltipHeight + CURSOR_OFFSET + SAFE_MARGIN) {
      y = rect.top - tooltipHeight - CURSOR_OFFSET;
    } else if (spaceBelow >= tooltipHeight + CURSOR_OFFSET + SAFE_MARGIN) {
      y = rect.bottom + CURSOR_OFFSET;
    } else {
      // tight viewport: clamp inside, anchored to the top
      y = Math.max(SAFE_MARGIN, window.innerHeight - tooltipHeight - SAFE_MARGIN);
    }

    const badgeCenter = rect.left + rect.width / 2;
    let x = badgeCenter - TOOLTIP_WIDTH / 2;
    x = Math.max(SAFE_MARGIN, Math.min(x, window.innerWidth - TOOLTIP_WIDTH - SAFE_MARGIN));

    setPos({ x, y, ready: true });
  }, [showTooltip]);

  if (value === undefined || value === null) {
    return <span className="text-white/20 text-xs">-</span>;
  }

  // Compute an immediate fallback position when mouse enters, so the tooltip
  // becomes visible on the first frame. useLayoutEffect then refines it using
  // the real measured height.
  const handleEnter = () => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const badgeCenter = rect.left + rect.width / 2;
      let x = badgeCenter - TOOLTIP_WIDTH / 2;
      x = Math.max(SAFE_MARGIN, Math.min(x, window.innerWidth - TOOLTIP_WIDTH - SAFE_MARGIN));
      let y = rect.top - 80 - CURSOR_OFFSET; // assume 80 px until refined
      if (y < SAFE_MARGIN) y = rect.bottom + CURSOR_OFFSET;
      setPos({ x, y, ready: true });
    }
    setShowTooltip(true);
  };

  return (
    <span
      ref={badgeRef}
      className="relative inline-flex items-center gap-1 cursor-default"
      data-testid="audience-impact-badge"
      onMouseEnter={handleEnter}
      onMouseLeave={() => { setShowTooltip(false); setPos((p) => ({ ...p, ready: false })); }}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${config.color}`} />
      <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium ${config.textColor}`}>
        {typeof value === 'number' ? value.toFixed(1) : value}
      </span>
      {showTooltip && createPortal(
        <span
          ref={tooltipRef}
          data-testid="audience-impact-tooltip"
          style={{
            position: 'fixed',
            left: `${pos.x}px`,
            top: `${pos.y}px`,
            width: `${TOOLTIP_WIDTH}px`,
            visibility: pos.ready ? 'visible' : 'hidden',
            zIndex: 999999,
            pointerEvents: 'none',
          }}
          className="px-3 py-2 text-xs text-white bg-gray-900 border border-gray-700 rounded-lg shadow-xl whitespace-pre-line block"
        >
          {config.tooltip}
        </span>,
        document.body
      )}
    </span>
  );
};

export default AudienceImpactBadge;
